import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const LoginPage = () => {
  const [companyCodeInput, setCompanyCodeInput] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const { companyCode, isResolved, setCompanyByCode } = useCompany();
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (isAuthenticated && isResolved && companyCode) {
    return <Navigate to={`/${companyCode}/dispatch`} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const code = companyCodeInput.trim().toLowerCase();
    if (!code) {
      setError('Please enter your company code.');
      return;
    }
    if (!username.trim() || !password.trim()) {
      setError('Please enter your username and password.');
      return;
    }

    setIsLoading(true);
    try {
      // Step 1: Resolve company code
      const companyData = await setCompanyByCode(code);
      if (!companyData) {
        setError('Company code not found. Please check and try again.');
        setIsLoading(false);
        return;
      }

      // Step 2: Log in with credentials
      const success = await login(username.trim(), password);
      if (!success) {
        setError('Invalid username or password.');
        setIsLoading(false);
        return;
      }

      navigate(`/${code}/dispatch`, { replace: true });
    } catch {
      setError('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Card sx={{ maxWidth: 420, width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                Dispatcher Login
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome back! Please sign in to continue.
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              method="post"
              action="#"
              autoComplete="on"
              sx={{ mt: 1 }}
            >
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="companyCode"
                label="Company Code"
                name="companyCode"
                autoFocus
                value={companyCodeInput}
                onChange={(e) => { setCompanyCodeInput(e.target.value); setError(''); }}
                placeholder="e.g. shias"
                disabled={isLoading}
                autoCapitalize="none"
                autoCorrect="off"
                slotProps={{ htmlInput: { autoComplete: 'off', 'data-form-type': 'other' } }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your name or email"
                slotProps={{ htmlInput: { autoComplete: 'username', name: 'username' } }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter your password"
                slotProps={{
                  htmlInput: { autoComplete: 'current-password', name: 'password' },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  mb: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Forgot your password? Contact your administrator.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginPage;
